"use client";

import React, { useEffect } from "react";
import { useCookieConsent, CookiePreferences } from "@/lib/cookie-consent";
import { X } from "lucide-react";
import Link from "next/link";

const CookiePreferencesModal: React.FC = () => {
  const {
    preferences,
    showModal,
    setShowModal,
    updatePreferences,
    savePreferences,
    acceptAll,
    rejectAll,
  } = useCookieConsent();

  // Listen for the showCookieModal event
  useEffect(() => {
    const handleShowModal = () => {
      setShowModal(true);
    };

    window.addEventListener("showCookieModal", handleShowModal);

    return () => {
      window.removeEventListener("showCookieModal", handleShowModal);
    };
  }, [setShowModal]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showModal]);

  if (!showModal) {
    return null;
  }

  const handleToggle = (key: keyof CookiePreferences) => {
    // Essential cookies can't be disabled
    if (key === "essential") return;

    updatePreferences({
      [key]: !preferences[key],
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-black text-white w-full sm:max-w-2xl sm:w-full max-h-full sm:max-h-[90svh] overflow-auto shadow-xl">
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 sm:p-6 border-b border-[#252525] bg-black">
          <h2 className="text-lg sm:text-xl font-medium">Cookie Preferences</h2>
          <button
            onClick={() => setShowModal(false)}
            className="p-2 hover:bg-[#252525] rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          <p className="mb-6 text-sm sm:text-base">
            This website uses cookies, pixel tags, and local storage for
            performance and marketing purposes. We use our own cookies and some
            from third parties. If you opted to accept any non-essential
            cookies, you can change or customize your selection at any time by
            changing the boxes checked below. To learn more about our privacy
            practices, please see our{" "}
            <Link href="/privacy-policy" className="underline">
              Privacy Policy
            </Link>
            .
          </p>

          <div className="space-y-4 sm:space-y-6">
            {/* Essential Cookies */}
            <div className="p-3 sm:p-4 border border-[#252525]">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="font-medium text-sm sm:text-base">ESSENTIAL</h3>
                <div
                  className={`w-10 sm:w-12 h-5 sm:h-6 rounded-full bg-[#252525] relative ${preferences.essential ? "bg-white" : ""}`}
                >
                  <div
                    className={`absolute top-1 left-1 bg-black w-3 sm:w-4 h-3 sm:h-4 rounded-full transform transition-transform ${
                      preferences.essential
                        ? "translate-x-5 sm:translate-x-6 bg-black"
                        : ""
                    }`}
                  />
                </div>
              </div>
              <p className="text-xs sm:text-sm">
                These items are necessary for the website to work.
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="p-3 sm:p-4 border border-[#252525]">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="font-medium text-sm sm:text-base">MARKETING</h3>
                <button
                  onClick={() => handleToggle("marketing")}
                  className={`w-10 sm:w-12 h-5 sm:h-6 rounded-full ${preferences.marketing ? "bg-white" : "bg-[#252525]"} relative`}
                >
                  <div
                    className={`absolute top-1 left-1 bg-black w-3 sm:w-4 h-3 sm:h-4 rounded-full transform transition-transform ${
                      preferences.marketing
                        ? "translate-x-5 sm:translate-x-6"
                        : ""
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs sm:text-sm">
                These items help deliver advertising that&apos;s more relevant
                to you. They may also be used to limit the number of times you
                see an ad and measure the effectiveness of ad campaigns. These
                are third party cookies provided by our advertising partners.
              </p>
            </div>

            {/* Personalization Cookies */}
            <div className="p-3 sm:p-4 border border-[#252525]">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="font-medium text-sm sm:text-base">
                  PERSONALIZATION
                </h3>
                <button
                  onClick={() => handleToggle("personalization")}
                  className={`w-10 sm:w-12 h-5 sm:h-6 rounded-full ${preferences.personalization ? "bg-white" : "bg-[#252525]"} relative`}
                >
                  <div
                    className={`absolute top-1 left-1 bg-black w-3 sm:w-4 h-3 sm:h-4 rounded-full transform transition-transform ${
                      preferences.personalization
                        ? "translate-x-5 sm:translate-x-6"
                        : ""
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs sm:text-sm">
                These items let the website remember choices you make (like user
                name, language, or region) and personalize features for you. For
                example, we may show you different content depending on which of
                our sites you&apos;ve visited and content you&apos;ve read.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="p-3 sm:p-4 border border-[#252525]">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="font-medium text-sm sm:text-base">ANALYTICS</h3>
                <button
                  onClick={() => handleToggle("analytics")}
                  className={`w-10 sm:w-12 h-5 sm:h-6 rounded-full ${preferences.analytics ? "bg-white" : "bg-[#252525]"} relative`}
                >
                  <div
                    className={`absolute top-1 left-1 bg-black w-3 sm:w-4 h-3 sm:h-4 rounded-full transform transition-transform ${
                      preferences.analytics
                        ? "translate-x-5 sm:translate-x-6"
                        : ""
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs sm:text-sm">
                These items help us understand visitor interactions, measure
                website performance, and spot potential technical issues. These
                don&apos;t include essential cookies that provide incidental
                analytics while making the website work.
              </p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-black p-4 sm:p-6 flex flex-col sm:flex-row justify-end gap-3 border-t border-[#252525]">
          <button
            onClick={rejectAll}
            className="py-2 sm:py-2 px-4 border border-white hover:bg-white hover:text-black transition-colors text-sm"
          >
            Reject all
          </button>
          <button
            onClick={acceptAll}
            className="py-2 sm:py-2 px-4 border border-white hover:bg-white hover:text-black transition-colors text-sm"
          >
            Accept all
          </button>
          <button
            onClick={savePreferences}
            className="py-2 sm:py-2 px-4 bg-white text-black hover:bg-gray-200 transition-colors text-sm"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferencesModal;
